import { makeAutoObservable } from 'mobx';
import { getPersistedValue } from 'helpers';
import { IVervAPIServices, Status, WebCatalogProductResponse } from 'utils/services/VervAPI';
import { IPaymentData, IPaymentModalProduct } from './types';
import 'core-js/features/object/from-entries';

export interface IPaymentStore {
	product: IPaymentModalProduct;
	insufficientFunds: boolean;
	insufficientFundsOfferWasShown: boolean;
	insufficientFundsLastOfferWasShown: boolean;
	isQuickPaymentAvailable: boolean;
	isAppleGooglePaymentValue: string;
	promocodeActivated: boolean;
	fixedDiscountPercent: number;
	isBraintreeVersion: boolean;
	isOldFixedPrice: number;
	isOldFixedPricePerDay: number;
	isPaymentModalVisible: boolean;
	isTwoModelsPrice: boolean;
	isTwoModelsPriceMode: boolean;
	isShowTimer: boolean;
	isShowDiscountPlans: boolean;

	settings: IPaymentSettings;

	setSearchParams: (params: URLSearchParams) => void;
	setPaymentProductAction: (productName: string) => void;
	setPromocodeActivated: (value: boolean) => void;
	insufficientFundsAction: (value: boolean) => void;
	insufficientFundsOfferShownAction: (value: boolean) => void;
	insufficientFundsLastOfferShownAction: (value: boolean) => void;
	setFixedDiscountPercent: (value: number) => void;
	setAvailabilityQuickPayment: (value: boolean) => void;
	setAppleGooglePaymentValue: (value: string) => void;
	setBraintreeVersion: (value: boolean) => void;
	setOldFixedPrice: (value: number) => void;
	setOldFixedPricePerDay: (value: number) => void;
	setPaymentModalVisible: (value: boolean) => void;
	setTwoModelsPrice: (value: boolean) => void;
	setTwoModelsPriceMode: (value: boolean) => void;
	setShowTimer: (value: boolean) => void;
	setShowDiscountPlans: (value: boolean) => void;
}

enum STORAGE_KEYS {
	product = 'product',
}

interface IPaymentSettings {
	currencyType: string;
	safetyAssurance: boolean;
	moreAccentToPrice: boolean;
	moneyBack: boolean;
	pdfOffer: string;
	planV2: boolean;
	noPaypal: boolean;
	promocodeAnimated: boolean;
	organicPricesV25: boolean;
	organicPricesV26: boolean;
	organicPricesV27: boolean;
	organicPricesV28: boolean;
	organicPricesV29: boolean;
	organicPricesV30: boolean;
	organicPricesV31: boolean;
	walkingDesignV1: boolean;
	walkingDesignV2: boolean;
	pilatesV1: boolean;
	pilatesV2: boolean;
	pilatesV3: boolean;
	pilatesV4: boolean;
	pilatesV5: boolean;
	mailSingleV4: boolean;
	welcomeTrialV1: boolean;
	fsv1f1: boolean;
	fsv1f2: boolean;
	fsv1f3: boolean;
	fsv1m1: boolean;
	fsv1m2: boolean;
	fsv1m3: boolean;
	fsv1f4: boolean;
	fsv1m4: boolean;
	fsv1f5: boolean;
	fsv1f6: boolean;
	fsv1f7: boolean;
	fsv1f8: boolean;
	fsv1f9: boolean;
	fsv1f10: boolean;
	fsv1m10: boolean;
	apc: boolean;
	primer: boolean;
}

export class PaymentStore implements IPaymentStore {
	settings: IPaymentSettings;
	product: IPaymentModalProduct;
	VervAPI: IVervAPIServices;
	insufficientFunds = false;
	insufficientFundsOfferWasShown = false;
	insufficientFundsLastOfferWasShown = false;
	isQuickPaymentAvailable = false;
	isAppleGooglePaymentValue = '';
	promocodeActivated = false;
	fixedDiscountPercent = 25;
	isBraintreeVersion = false;
	isOldFixedPrice = 0;
	isOldFixedPricePerDay = 0;
	isPaymentModalVisible = false;
	isTwoModelsPrice = false;
	isTwoModelsPriceMode = false;
	isShowTimer = false;
	isShowDiscountPlans = false;

	constructor(params: URLSearchParams, storage: Storage, api: IVervAPIServices) {
		makeAutoObservable(this);
		this.settings = getSettings(params);
		this.product = getPersistedProduct(storage);
		this.VervAPI = api;
	}

	setSearchParams = (searchParams: URLSearchParams): void => {
		this.settings = getSettings(searchParams);
	};

	setPaymentProductAction = (productName: string): void => {
		void this.VervAPI.getWebCatalogProduct(productName).then((data: WebCatalogProductResponse) => {
			// do not save nothing
			if (data.status !== Status.success) return;
			const productDetails = data.data;

			this.setPaymentProduct({
				name: productName,
				details: productDetails,
			});
		});
	};

	setPaymentProduct = (value: IPaymentModalProduct): void => {
		this.product = value;
	};

	setPromocodeActivated = (value: boolean): void => {
		this.promocodeActivated = value;
	};

	insufficientFundsAction = (value: boolean): void => {
		this.insufficientFunds = value;
	};

	insufficientFundsOfferShownAction = (value: boolean): void => {
		this.insufficientFundsOfferWasShown = value;
	};

	insufficientFundsLastOfferShownAction = (value: boolean): void => {
		this.insufficientFundsLastOfferWasShown = value;
	};

	setFixedDiscountPercent = (value: number): void => {
		this.fixedDiscountPercent = value;
	};

	setAvailabilityQuickPayment = (value: boolean): void => {
		this.isQuickPaymentAvailable = value;
	};

	setAppleGooglePaymentValue = (value: string): void => {
		this.isAppleGooglePaymentValue = value;
	};

	setBraintreeVersion = (value: boolean): void => {
		this.isBraintreeVersion = value;
	};

	setOldFixedPrice = (value: number): void => {
		this.isOldFixedPrice = value;
	};

	setOldFixedPricePerDay = (value: number): void => {
		this.isOldFixedPricePerDay = value;
	};

	setPaymentModalVisible = (value: boolean): void => {
		this.isPaymentModalVisible = value;
	};

	setTwoModelsPrice = (value: boolean): void => {
		this.isTwoModelsPrice = value;
	};

	setTwoModelsPriceMode = (value: boolean): void => {
		this.isTwoModelsPriceMode = value;
	};

	setShowTimer = (value: boolean): void => {
		this.isShowTimer = value;
	};

	setShowDiscountPlans = (value: boolean): void => {
		this.isShowDiscountPlans = value;
	};
}

const getPersistedProduct = (storage: Storage): IPaymentModalProduct => {
	const paymentProduct: IPaymentData | undefined = getPersistedValue(STORAGE_KEYS.product, storage);

	return {
		name: paymentProduct?.name,
		details: paymentProduct?.details,
	};
};

const getSettings = (searchParams: URLSearchParams): IPaymentSettings => {
	const sourceParameters = Object.fromEntries(searchParams.entries());

	const currencyType =
		sourceParameters.organicPricesV3 === 'true' ||
		sourceParameters.day0paybackV9 === 'true' ||
		sourceParameters.day0paybackV10 === 'true'
			? '€'
			: sourceParameters.usdToDollar === 'true' ||
			  sourceParameters.pilatesV1 ||
			  sourceParameters.pilatesV2 ||
			  sourceParameters.pilatesV3 ||
			  sourceParameters.pilatesV4 ||
			  sourceParameters.pilatesV5
			? '$'
			: 'USD ';

	return {
		currencyType,
		safetyAssurance: sourceParameters.safetyAssurance === 'true',
		moreAccentToPrice: sourceParameters.moreAccentToPrice === 'true',
		pdfOffer: sourceParameters.pdfOffer,
		moneyBack: sourceParameters.moneyBack === 'true',
		planV2: sourceParameters.planV2 === 'true',
		noPaypal: sourceParameters.nopp === 'true',
		promocodeAnimated: sourceParameters.promo === 'act',
		organicPricesV25: sourceParameters.organicPricesV25 === 'true',
		organicPricesV26: sourceParameters.organicPricesV26 === 'true',
		organicPricesV27: sourceParameters.organicPricesV27 === 'true',
		organicPricesV28: sourceParameters.organicPricesV28 === 'true',
		organicPricesV29: sourceParameters.organicPricesV29 === 'true',
		organicPricesV30: sourceParameters.organicPricesV30 === 'true',
		organicPricesV31: sourceParameters.organicPricesV31 === 'true',
		walkingDesignV1: sourceParameters.walkingDesignV1 === 'true',
		walkingDesignV2: sourceParameters.walkingDesignV2 === 'true',
		pilatesV1: sourceParameters.pilatesV1 === 'true',
		pilatesV2: sourceParameters.pilatesV2 === 'true',
		pilatesV3: sourceParameters.pilatesV3 === 'true',
		pilatesV4: sourceParameters.pilatesV4 === 'true',
		pilatesV5: sourceParameters.pilatesV5 === 'true',
		mailSingleV4: sourceParameters.mailSingleV4 === 'true',
		welcomeTrialV1: sourceParameters.welcomeTrialV1 === 'true',
		fsv1f1: sourceParameters.fsv1f1 === 'true',
		fsv1f2: sourceParameters.fsv1f2 === 'true',
		fsv1f3: sourceParameters.fsv1f3 === 'true',
		fsv1m1: sourceParameters.fsv1m1 === 'true',
		fsv1m2: sourceParameters.fsv1m2 === 'true',
		fsv1m3: sourceParameters.fsv1m3 === 'true',
		fsv1f4: sourceParameters.fsv1f4 === 'true',
		fsv1m4: sourceParameters.fsv1m4 === 'true',
		fsv1f5: sourceParameters.fsv1f5 === 'true',
		fsv1f6: sourceParameters.fsv1f6 === 'true',
		fsv1f7: sourceParameters.fsv1f7 === 'true',
		fsv1f8: sourceParameters.fsv1f8 === 'true',
		fsv1f9: sourceParameters.fsv1f9 === 'true',
		fsv1f10: sourceParameters.fsv1f10 === 'true',
		fsv1m10: sourceParameters.fsv1m10 === 'true',
		apc: sourceParameters.apc === 'true',
		primer: sourceParameters.primer === 'true',
	};
};
